import type { OptionType } from "$components/inputs";
import { SUPPLIER_OPTIONS } from "$components/supplier-labels";
import { useConvertFromPenceFactory } from "$routes/_lib/use-convert-currency";
import type { PriceList } from "$routes/patient/[:patientId]/_lib/invoices/components/InvoiceCard/types";
import { useRevenueDepartments } from "$routes/patient/[:patientId]/_lib/invoices-v2/use-revenue-departments";
import { format } from "date-fns";
import { useCallback, useMemo } from "react";
import type { BuildInvoiceViewArgs, InvoiceItemOption } from "./invoice-view";
import { priceListItemOptions, priceListItemsById } from "./invoice-view";
import { useInvoiceMoneyFormat } from "./use-invoice-money-format";

export const formatInvoiceDate = (date: Date): string =>
  format(date, "MMM d, yyyy");

const formatItemDate = (date: Date): string => format(date, "d MMM");
const formatTimestamp = (date: Date): string => format(date, "d MMM, HH:mm");

type InvoiceViewEnvironment = Pick<
  BuildInvoiceViewArgs,
  | "toMajor"
  | "formatMoney"
  | "departmentNameById"
  | "formatItemDate"
  | "formatTimestamp"
>;

export const useInvoiceCardData = (
  priceList: PriceList,
  invoiceCurrency: string | null | undefined,
): InvoiceViewEnvironment & {
  itemsById: ReturnType<typeof priceListItemsById>;
  itemOptions: InvoiceItemOption[];
  departmentOptions: OptionType[];
  supplierOptions: OptionType[];
} => {
  const convertFromPence = useConvertFromPenceFactory();
  const formatMoney = useInvoiceMoneyFormat(invoiceCurrency);
  const { options: departmentOptions, nameById } = useRevenueDepartments();

  const toMajor = useCallback(
    (amountInPence: number): number => Number(convertFromPence(amountInPence)),
    [convertFromPence],
  );

  return useMemo(
    () => ({
      toMajor,
      formatMoney,
      departmentNameById: nameById,
      formatItemDate,
      formatTimestamp,
      itemsById: priceListItemsById(priceList),
      itemOptions: priceListItemOptions(priceList),
      departmentOptions,
      supplierOptions: SUPPLIER_OPTIONS,
    }),
    [departmentOptions, formatMoney, nameById, priceList, toMajor],
  );
};
